'use client'

import { useEffect } from 'react'
import { ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { logClientError } from 'app/actions/clientErrorLogging'

function getClientDebugContext() {
    if (typeof window === 'undefined') return {}
    const tz = Intl.DateTimeFormat?.().resolvedOptions?.()?.timeZone
    return {
        userAgent: navigator.userAgent ?? null,
        language: navigator.language ?? null,
        timezone: tz ?? null
    }
}

export function ErrorFallback({ error, onResetAction }: { error: Error; onResetAction?: () => void }) {
    const is502 = error instanceof Response && (error as unknown as { status?: number }).status === 502

    useEffect(() => {
        if (is502) return
        if (typeof window === 'undefined') return

        const ctx = getClientDebugContext()
        const base = {
            message: error.message,
            stack: error.stack ?? null,
            url: window.location.href,
            referrer: document.referrer || '',
            ...ctx
        }

        // Fetch build info so we know which deploy had the error (no await – log immediately with context, then enrich if API responds)
        fetch('/api/build-info')
            .then(r => r.json())
            .then((info: Record<string, unknown>) => {
                logClientError({
                    ...base,
                    buildTimestamp: (info.buildTimestamp as string) ?? null,
                    gitCommitHash: (info.gitCommitHash as string) ?? null,
                    vercelDeploymentId: (info.vercelDeploymentId as string) ?? null,
                    vercelRegion: (info.vercelRegion as string) ?? null,
                    // Error reached the boundary – treat as uncaught crash with high severity
                    caught: false,
                    severity: 'error'
                })
            })
            .catch(() =>
                logClientError({
                    ...base,
                    caught: false,
                    severity: 'error'
                })
            )
    }, [error.message, error.stack, is502])

    if (is502) {
        return (
            <div className='min-h-screen flex items-center justify-center'>
                <div className='text-center'>
                    <h1 className='text-4xl font-bold mb-4'>Server Unavailable 502</h1>
                    <p className='text-muted-foreground mb-4'>Our servers are currently down for maintenance.</p>
                    <p className='text-muted-foreground'>Please try again in a few minutes.</p>
                    <button
                        onClick={() => window.location.reload()}
                        className='mt-4 px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90'
                    >
                        Retry
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className='min-h-screen flex items-center justify-center'>
            <div className='text-center'>
                <h1 className='text-4xl font-bold mb-4'>Something went wrong</h1>
                <p className='text-muted-foreground'>{error.message}</p>
                {onResetAction && (
                    <button
                        onClick={onResetAction}
                        className='mt-4 px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90'
                    >
                        Try again
                    </button>
                )}
            </div>
        </div>
    )
}

const ErrorBoundaryWrapper = ({ children }: { children: ReactNode }) => {
    return (
        <ErrorBoundary
            FallbackComponent={({ error, resetErrorBoundary }) => (
                <ErrorFallback error={error} onResetAction={resetErrorBoundary} />
            )}
        >
            {children}
        </ErrorBoundary>
    )
}

export default ErrorBoundaryWrapper
